/**
 * HustleRoundupRoomPage — full-viewport LiveKit room with host controls,
 * persistent chat side-pane, waiting-room approvals, and recording toggle.
 *
 * URL: /meet/:roomName
 *
 * The page is deliberately a viewport takeover — <PlatformHeader> is NOT
 * rendered here (per §14 of the rebuild doc).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  LiveKitRoom, VideoConference, RoomAudioRenderer,
} from "@livekit/components-react";
import "@livekit/components-styles";
import {
  Mic, MicOff, Video as VideoIcon, VideoOff, Circle, Square,
  Lock, Unlock, VolumeX, MessageSquare, Users, X, Send, LogOut,
  Sparkles, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { apiFetch, fetchMe, readToken, API } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function HustleRoundupRoomPage() {
  const { roomName } = useParams();
  const navigate     = useNavigate();

  const [me, setMe] = useState(null);
  const [connState, setConnState] = useState({
    loading: true, error: null, token: null, url: null, room: null, identity: null,
  });
  const [meeting, setMeeting] = useState(null);   // hp_roundup_meetings row (via bootstrap)
  const [roomInfo, setRoomInfo] = useState(null); // hp_roundup_rooms row (host controls)
  const [rightPane, setRightPane] = useState(null); // "chat" | "people" | null
  const [recording, setRecording] = useState(null); // {id, state} | null

  // ── Bootstrap ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await fetchMe();
      if (!u?.id) {
        navigate(`/login?next=/meet/${encodeURIComponent(roomName)}`);
        return;
      }
      if (cancelled) return;
      setMe(u);
      try {
        const data = await apiFetch("/hustleroundup/token", {
          method: "POST",
          body: JSON.stringify({ room: roomName, name: u.name || u.email }),
        });
        if (cancelled) return;
        setConnState({ loading: false, error: null, ...data });
        // Try to find/attach the room row (owner controls + waiting list)
        try {
          const r = await apiFetch(`/hustleroundup/rooms/${encodeURIComponent(roomName)}`);
          setRoomInfo(r);
        } catch { /* room may not exist in DB yet — instant meeting */ }
        // Best-effort: open a bootstrap meeting row via webhook simulate
        // (this is the same code path LiveKit would fire when room_started
        // arrives — we prime it so chat/recording routes have a meeting id).
        try {
          await fetch(`${API}/hustleroundup/livekit-webhook`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: `boot_${Date.now()}`,
              event: "room_started",
              room: { name: data.room },
            }),
          });
        } catch { /* ignore */ }
        // Find the meeting row we just created / that already exists
        try {
          const hist = await apiFetch("/hustleroundup/history/mine?limit=5");
          const match = (hist || []).find((m) => m.livekit_room === data.room && !m.ended_at);
          if (match) setMeeting(match);
        } catch { /* ignore */ }
      } catch (err) {
        if (!cancelled) setConnState({ loading: false, error: err.message || "Token error" });
      }
    })();
    return () => { cancelled = true; };
  }, [roomName, navigate]);

  const isHost = useMemo(() => {
    if (!me || !roomInfo) return false;
    return (roomInfo.host_ids || []).includes(me.id) || roomInfo.owner_id === me.id;
  }, [me, roomInfo]);

  // ── Host actions ─────────────────────────────────────────────────
  const startRecording = async () => {
    if (!meeting) { toast.error("Meeting not ready yet"); return; }
    try {
      const rec = await apiFetch(`/hustleroundup/meetings/${meeting.id}/recording/start`, { method: "POST" });
      setRecording({ id: rec.id, state: rec.state });
      if (rec.state === "failed") toast.error(`Recording start failed: ${rec.error || "unknown"}`);
      else toast.success("Recording started");
    } catch (e) { toast.error(e.message); }
  };
  const stopRecording = async () => {
    if (!meeting) return;
    try {
      await apiFetch(`/hustleroundup/meetings/${meeting.id}/recording/stop`, { method: "POST" });
      setRecording({ ...(recording || {}), state: "uploading" });
      toast.success("Recording stopped — processing");
    } catch (e) { toast.error(e.message); }
  };
  const toggleLock = async () => {
    if (!meeting) return;
    try {
      const res = await apiFetch(`/hustleroundup/meetings/${meeting.id}/lock`, { method: "POST" });
      setRoomInfo((r) => (r ? { ...r, locked: res.locked } : r));
      toast.success(res.locked ? "Room locked" : "Room unlocked");
    } catch (e) { toast.error(e.message); }
  };
  const muteAll = async () => {
    if (!meeting) return;
    try {
      const res = await apiFetch(`/hustleroundup/meetings/${meeting.id}/mute-all`, { method: "POST" });
      toast.success(`Muted ${res.muted || 0} participant(s)`);
    } catch (e) { toast.error(e.message); }
  };
  const requestTranscript = async () => {
    if (!meeting) return;
    try {
      await apiFetch(`/hustleroundup/meetings/${meeting.id}/transcript`, { method: "POST" });
      toast.success("Transcript job queued");
    } catch (e) { toast.error(e.message); }
  };

  const copyInvite = () => {
    if (!roomInfo?.join_code) {
      navigator.clipboard.writeText(`${window.location.origin}/meet/${roomName}`);
      toast.success("Meeting link copied");
    } else {
      navigator.clipboard.writeText(`${window.location.origin}/join/${roomInfo.join_code}`);
      toast.success("Join-code link copied");
    }
  };

  // ── Render ───────────────────────────────────────────────────────
  if (connState.loading) {
    return (
      <div data-testid="hustleroundup-loading"
           className="min-h-screen flex items-center justify-center bg-stone-950 text-white">
        <div className="text-center">
          <div className="animate-pulse text-lg font-semibold">Joining meeting…</div>
          <div className="text-sm text-stone-400 mt-2">Room: <code>{roomName}</code></div>
        </div>
      </div>
    );
  }
  if (connState.error) {
    return (
      <div data-testid="hustleroundup-error"
           className="min-h-screen flex items-center justify-center bg-stone-950 text-white p-8">
        <div className="max-w-md text-center">
          <div className="text-2xl font-bold mb-3">Couldn&apos;t join</div>
          <div className="text-sm text-stone-400 mb-4">{connState.error}</div>
          <button
            data-testid="hustleroundup-error-home"
            onClick={() => navigate("/")}
            className="px-4 py-2 rounded-full bg-white text-stone-950 font-semibold text-sm"
          >Back home</button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="hustleroundup-room"
      className="fixed inset-0 flex flex-col"
      style={{ background: "#0a0a0a", color: "#fff" }}
    >
      {/* Slim top status bar */}
      <div className="h-11 flex items-center gap-2 px-3 border-b border-stone-800 bg-stone-950 text-xs">
        <span className="font-semibold tracking-tight">HustleRoundup</span>
        <span className="text-stone-500">·</span>
        <code className="text-emerald-400">{roomName}</code>
        {roomInfo?.locked && (
          <span data-testid="room-locked-badge" className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-[10px] uppercase tracking-widest">
            <Lock className="w-3 h-3" /> Locked
          </span>
        )}
        {recording?.state && recording.state !== "failed" && (
          <span data-testid="room-recording-badge" className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 text-[10px] uppercase tracking-widest">
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" /> Recording
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <IconBtn onClick={copyInvite} testid="room-copy-invite" title="Copy invite">
            <Copy className="w-4 h-4" />
          </IconBtn>
          <IconBtn onClick={() => setRightPane(rightPane === "chat" ? null : "chat")}
                   testid="room-toggle-chat" title="Chat"
                   active={rightPane === "chat"}>
            <MessageSquare className="w-4 h-4" />
          </IconBtn>
          {isHost && (
            <IconBtn onClick={() => setRightPane(rightPane === "people" ? null : "people")}
                     testid="room-toggle-people" title="Waiting room"
                     active={rightPane === "people"}>
              <Users className="w-4 h-4" />
            </IconBtn>
          )}
          <IconBtn onClick={() => navigate("/")} testid="room-leave" title="Leave">
            <LogOut className="w-4 h-4" />
          </IconBtn>
        </div>
      </div>

      {/* Host controls strip (only for hosts) */}
      {isHost && (
        <div className="h-11 flex items-center gap-2 px-3 border-b border-stone-800 bg-stone-950/80 text-xs">
          {recording?.state && recording.state !== "failed" && recording.state !== "ready" ? (
            <ActionBtn onClick={stopRecording} testid="hustleroundup-recording-stop-btn" tone="danger">
              <Square className="w-3.5 h-3.5" /> Stop recording
            </ActionBtn>
          ) : (
            <ActionBtn onClick={startRecording} testid="hustleroundup-recording-btn">
              <Circle className="w-3.5 h-3.5 text-red-400" /> Start recording
            </ActionBtn>
          )}
          <ActionBtn onClick={muteAll} testid="hustleroundup-mute-all-btn">
            <VolumeX className="w-3.5 h-3.5" /> Mute all
          </ActionBtn>
          <ActionBtn onClick={toggleLock} testid="hustleroundup-lock-btn">
            {roomInfo?.locked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
            {roomInfo?.locked ? " Unlock room" : " Lock room"}
          </ActionBtn>
          <ActionBtn onClick={requestTranscript} testid="hustleroundup-transcript-btn">
            <Sparkles className="w-3.5 h-3.5" /> Generate transcript
          </ActionBtn>
          {meeting && (
            <span className="ml-auto text-[10px] text-stone-500">meeting <code>{meeting.id}</code></span>
          )}
        </div>
      )}

      {/* LiveKit room + optional right pane */}
      <div className="flex-1 flex min-h-0">
        <div className="flex-1 min-h-0 relative">
          <LiveKitRoom
            serverUrl={connState.url}
            token={connState.token}
            connect
            video
            audio
            onDisconnected={() => navigate("/")}
            data-lk-theme="default"
            style={{ height: "100%" }}
          >
            <VideoConference />
            <RoomAudioRenderer />
          </LiveKitRoom>
        </div>
        {rightPane === "chat" && meeting && (
          <ChatPane meeting={meeting} onClose={() => setRightPane(null)} me={me} />
        )}
        {rightPane === "people" && meeting && isHost && (
          <PeoplePane meeting={meeting} onClose={() => setRightPane(null)} />
        )}
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────
function IconBtn({ onClick, children, testid, title, active }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      title={title}
      className={`p-2 rounded-md ${active ? "bg-emerald-500/20 text-emerald-300" : "text-stone-300 hover:bg-stone-800"}`}
    >
      {children}
    </button>
  );
}

function ActionBtn({ onClick, children, testid, tone }) {
  const cls = tone === "danger"
    ? "bg-red-500/15 text-red-200 hover:bg-red-500/25"
    : "bg-stone-800 text-stone-200 hover:bg-stone-700";
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium ${cls}`}
    >
      {children}
    </button>
  );
}

function ChatPane({ meeting, onClose, me }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const wsRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const hist = await apiFetch(`/hustleroundup/meetings/${meeting.id}/chat`);
        setMsgs(hist || []);
      } catch { /* ignore */ }
    })();
  }, [meeting.id]);

  useEffect(() => {
    const token = readToken();
    const wsUrl = BACKEND.replace(/^https/, "wss").replace(/^http/, "ws")
      + `/api/hustleroundup/meetings/${meeting.id}/chat/live?token=${encodeURIComponent(token || "")}`;
    let ws;
    try { ws = new WebSocket(wsUrl); } catch { return; }
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const p = JSON.parse(ev.data);
        if (p.type === "chat.message") {
          setMsgs((prev) => (prev.find((m) => m.id === p.message.id) ? prev : [...prev, p.message]));
        }
      } catch { /* ignore */ }
    };
    return () => { try { ws.close(); } catch { /* ignore */ } };
  }, [meeting.id]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setText("");
    // Prefer WS if open, else REST fallback
    if (wsRef.current?.readyState === 1) {
      wsRef.current.send(JSON.stringify({ text: t, name: me?.name }));
    } else {
      try {
        const msg = await apiFetch(`/hustleroundup/meetings/${meeting.id}/chat`, {
          method: "POST", body: JSON.stringify({ text: t }),
        });
        setMsgs((prev) => [...prev, msg]);
      } catch (e) { toast.error(e.message); }
    }
  };

  return (
    <aside data-testid="hustleroundup-chat-pane"
           className="w-80 border-l border-stone-800 bg-stone-950 flex flex-col">
      <header className="h-11 px-3 border-b border-stone-800 flex items-center justify-between text-xs">
        <span className="font-semibold">Chat</span>
        <button onClick={onClose} data-testid="chat-close"
                className="p-1 rounded-md hover:bg-stone-800"><X className="w-4 h-4" /></button>
      </header>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {msgs.length === 0 && (
          <div className="text-xs text-stone-500 text-center pt-6">No messages yet.</div>
        )}
        {msgs.map((m) => (
          <div key={m.id} data-testid={`chat-msg-${m.id}`}
               className="bg-stone-900 border border-stone-800 rounded-lg p-2.5">
            <div className="flex items-baseline justify-between mb-0.5">
              <span className="text-[11px] font-semibold text-emerald-300">{m.author_name}</span>
              <span className="text-[10px] text-stone-500">{new Date(m.sent_at).toLocaleTimeString()}</span>
            </div>
            <div className="text-sm text-stone-200 break-words">{m.text}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="p-2 border-t border-stone-800 flex gap-2">
        <input
          data-testid="chat-input"
          value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Message"
          className="flex-1 px-2.5 py-2 rounded-md bg-stone-900 border border-stone-800 text-sm focus:border-emerald-500 outline-none"
        />
        <button onClick={send} data-testid="chat-send"
                className="px-3 rounded-md bg-emerald-500 text-stone-950">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </aside>
  );
}

function PeoplePane({ meeting, onClose }) {
  const [waiting, setWaiting] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const list = await apiFetch(`/hustleroundup/meetings/${meeting.id}/participants`);
      setWaiting((list || []).filter((p) => !p.approved && !p.kicked));
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); const i = setInterval(load, 4000); return () => clearInterval(i); }, [meeting.id]);

  const approve = async (pid) => {
    setBusy(pid);
    try { await apiFetch(`/hustleroundup/meetings/${meeting.id}/waiting/${pid}/approve`, { method: "POST" });
      toast.success("Approved");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); load(); }
  };
  const deny = async (pid) => {
    setBusy(pid);
    try { await apiFetch(`/hustleroundup/meetings/${meeting.id}/waiting/${pid}/deny`, { method: "POST" });
      toast.success("Denied");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); load(); }
  };

  return (
    <aside data-testid="hustleroundup-waiting-modal"
           className="w-80 border-l border-stone-800 bg-stone-950 flex flex-col">
      <header className="h-11 px-3 border-b border-stone-800 flex items-center justify-between text-xs">
        <span className="font-semibold">Waiting room ({waiting.length})</span>
        <button onClick={onClose} data-testid="people-close" className="p-1 rounded-md hover:bg-stone-800">
          <X className="w-4 h-4" />
        </button>
      </header>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {waiting.length === 0 && (
          <div className="text-xs text-stone-500 text-center pt-6">Nobody is waiting.</div>
        )}
        {waiting.map((p) => (
          <div key={p.id} data-testid={`waiting-row-${p.id}`}
               className="bg-stone-900 border border-stone-800 rounded-lg p-3">
            <div className="text-sm mb-2">{p.guest_name || p.identity}</div>
            <div className="flex gap-2">
              <button
                data-testid="hustleroundup-waiting-approve"
                disabled={busy === p.id}
                onClick={() => approve(p.id)}
                className="flex-1 px-3 py-1.5 rounded-md bg-emerald-500 text-stone-950 text-xs font-semibold"
              >Approve</button>
              <button
                data-testid="hustleroundup-waiting-deny"
                disabled={busy === p.id}
                onClick={() => deny(p.id)}
                className="flex-1 px-3 py-1.5 rounded-md border border-stone-700 text-xs"
              >Deny</button>
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}
